<!--
AppModal Component - Custom modal with mobile-optimized bottom sheet support

USAGE EXAMPLES:

1. Basic usage:
<AppModal v-model="showModal" :showHeader="true">
    <div>Modal content here</div>
</AppModal>

2. With custom ID:
<AppModal 
    id="myCustomModal"
    v-model="showModal" 
    :showHeader="true"
>
    <div>Content</div>
</AppModal>

3. Mobile app view (90vh bottom sheet with slide animation and drag-to-close):
<AppModal 
    v-model="showModal" 
    :showHeader="true" 
    mobileAppView
    size="md"
>
    <div>Your content</div>
</AppModal>

4. With footer slot:
<AppModal v-model="showModal" :showHeader="true" :showFooter="true">
    <div>Content</div>
    <template #footer>
        <button @click="showModal = false">Cancel</button>
        <button @click="handleSave">Save</button>
    </template>
</AppModal>

5. Fullscreen modal:
<AppModal v-model="showModal" :showHeader="true" fullscreen>
    <div>Fullscreen content</div>
</AppModal>

6. Prevent closing on backdrop click:
<AppModal 
    v-model="showModal" 
    :showHeader="true" 
    noCloseOnBackdrop
    noCloseOnEsc
>
    <div>Important content that requires explicit close</div>
</AppModal>

7. With ref for programmatic control:
<AppModal ref="modalRef" v-model="showModal" :showHeader="true">
    <div>Content</div>
</AppModal>

In script:
const modalRef = ref(null)
modalRef.value.show()  // Open modal
modalRef.value.hide()  // Close modal
-->

<template>
    <teleport to="body">
        <transition :name="mobileAppView ? 'app-modal-slide' : 'app-modal-fade'">
            <div v-if="modelValue" class="app-modal-overlay" :class="[`app-modal-overlay--${position}`, { 'app-modal-overlay--fullscreen': fullscreen, 'app-modal-overlay--fullscreen-mobile': shouldFullscreenMobile, 'app-modal-overlay--mobile-app-view': mobileAppView }]" @mousedown.self="handleOverlayClick">
                <div v-if="showHeaderOverlay" class="absolute w-100 overlay--header" style="top: 0; left: 0;">
                    <slot name="headerOverlay"></slot>
                </div>
                <div :id="id" ref="modalRef" class="app-modal" :class="[`app-modal--${size}`, { 'app-modal--fullscreen': fullscreen, 'app-modal--fullscreen-mobile': shouldFullscreenMobile, 'app-modal--mobile-app-view': mobileAppView }]" :style="[modalStyle, maxWidthOverride ? { maxWidth: maxWidthOverride } : {}]">
                    <div v-if="showHeader" class="app-modal__header">

                        <div class="pt-18 app-modal__slider-con" v-if="mobileAppView" @mousedown="handleDragStart" @touchstart="handleDragStart" style="touch-action: none;">
                            <div class="__slider" style="touch-action: none;"></div>
                        </div>

                        <div class="p-24 xs-p-16 app-modal__close-icon">
                            <el-icon :size="24" color="#30374180" class="text-grey cursor-pointer" @click="close">
                                <Close />
                            </el-icon>
                        </div>
                    </div>
                    <div class="app-modal__body scrollbar-hidden" :class="{ 'app-modal__body--no-scrollbar': hideScrollbar }">
                        <slot></slot>
                    </div>
                    <div v-if="showFooter" class="app-modal__footer">
                        <slot name="footer"></slot>
                    </div>
                </div>
            </div>
        </transition>
    </teleport>
</template>

<script setup>
import { ElIcon } from 'element-plus'
import { watch, computed, onMounted, onBeforeUnmount, ref } from 'vue'
import { Close } from '@element-plus/icons-vue'

defineOptions({ inheritAttrs: false })

const props = defineProps({
    id: {
        type: String,
        required: false,
        default: ''
    },
    modelValue: {
        type: Boolean,
        required: false,
        default: false
    },
    size: {
        type: String,
        required: false,
        default: 'sm',
        validator: (v) => ['sm', 'md', 'lg', 'xl'].includes(v)
    },
    position: {
        type: String,
        required: false,
        default: 'top',
        validator: (v) => ['top', 'center'].includes(v)
    },
    noCloseOnBackdrop: {
        type: Boolean,
        required: false,
        default: false
    },
    noCloseOnEsc: {
        type: Boolean,
        required: false,
        default: false
    },
    mobileAppView: {
        type: Boolean,
        required: false,
        default: false
    },
    fullscreen: {
        type: Boolean,
        required: false,
        default: false
    },
    fullScreenOnMobile: {
        type: Boolean,
        required: false,
        default: false
    },
    hideScrollbar: {
        type: Boolean,
        required: false,
        default: false
    },
    showHeaderOverlay: {
        type: Boolean,
        required: false,
        default: false
    },
    showHeader: {
        type: Boolean,
        required: false,
        default: false
    },
    showFooter: {
        type: Boolean,
        required: false,
        default: false
    },
    maxWidthOverride: {
        type: String,
        required: false,
        default: false
    }
})

const emit = defineEmits(['update:modelValue', 'close'])

// Refs
const modalRef = ref(null)

// Drag state
let isDragging = false
let startY = 0
let currentY = 0

const shouldFullscreenMobile = computed(() => {
    return props.fullScreenOnMobile && !props.mobileAppView
})

const modalStyle = computed(() => {
    // Don't apply maxHeight constraints in fullscreen modes
    if (props.fullscreen || shouldFullscreenMobile.value) {
        return {}
    }
    return {
        maxHeight: 'calc(100svh - 80px)',
        overflowY: 'auto'
    }
})

function close() {
    emit('update:modelValue', false)
    emit('close')
}

function show() {
    emit('update:modelValue', true)
}

function hide() {
    close()
}

function handleOverlayClick() {
    if (!props.noCloseOnBackdrop) {  
        close()
    }
}

function handleKeydown(e) {
    if (e.key === 'Escape' && props.modelValue && !props.noCloseOnEsc) {
        close()
    }
}

function handleDragStart(e) {
    isDragging = true
    startY = e.type === 'touchstart' ? e.touches[0].clientY : e.clientY
    currentY = startY

    document.addEventListener('mousemove', handleDragMove)
    document.addEventListener('touchmove', handleDragMove, { passive: false })
    document.addEventListener('mouseup', handleDragEnd)
    document.addEventListener('touchend', handleDragEnd)
}

function handleDragMove(e) {
    if (!isDragging || !modalRef.value) return

    currentY = e.type === 'touchmove' ? e.touches[0].clientY : e.clientY
    const deltaY = currentY - startY

    // Only allow dragging down
    if (deltaY > 0) {
        // Prevent browser pull-to-refresh when dragging modal down
        e.preventDefault()
        modalRef.value.style.transform = `translateY(${deltaY}px)`
        modalRef.value.style.transition = 'none'
    }
}

function handleDragEnd() {
    if (!isDragging || !modalRef.value) return

    const deltaY = currentY - startY
    const threshold = 100 // Close if dragged more than 100px down

    if (deltaY > threshold) {
        // Animate slide down completely before closing
        modalRef.value.style.transform = 'translateY(100%)'
        modalRef.value.style.transition = 'transform 0.25s ease-in'
        
        // Close after animation completes
        setTimeout(() => {
            close()
        }, 250)
    } else {
        // Snap back to original position
        modalRef.value.style.transform = ''
        modalRef.value.style.transition = 'transform 0.3s ease-out'
    }

    isDragging = false
    document.removeEventListener('mousemove', handleDragMove)
    document.removeEventListener('touchmove', handleDragMove, { passive: false })
    document.removeEventListener('mouseup', handleDragEnd)
    document.removeEventListener('touchend', handleDragEnd)
}

onMounted(() => {
    document.addEventListener('keydown', handleKeydown)
})

onBeforeUnmount(() => {
    document.removeEventListener('keydown', handleKeydown)
})

watch(() => props.modelValue, (val) => {
    document.body.style.overflow = val ? 'hidden' : ''
})

defineExpose({ show, hide, close })
</script>
